import { Theme, ThemeOptions } from '@mui/material';
import Clear from '@mui/icons-material/Clear';
import { createBaseTheme, themeColors } from '@ianesthesie-kit/config';
import { Emphasis } from '@/modules/surgicalEvent/model/emphasis';

/* eslint-disable @typescript-eslint/no-empty-interface */

declare module '@mui/material/styles' {
  interface Theme {
    graph?: {
      color?: string[];
    };
  }

  interface ThemeOptions {
    graph?: {
      color?: string[];
    };
  }
}

interface RiskColorOverrides {
  riskHigh: true;
  riskMed: true;
  riskNone: true;
}
/* eslint-disable @typescript-eslint/no-empty-object-type */

declare module '@mui/material/TextField' {
  interface TextFieldPropsColorOverrides extends RiskColorOverrides {}
}

declare module '@mui/material/InputBase' {
  interface InputBasePropsColorOverrides extends RiskColorOverrides {}
}

declare module '@mui/material/FormControl' {
  interface FormControlPropsColorOverrides extends RiskColorOverrides {}
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    suggestion: true;
    empty: true;
    medicationName: true;
    slateIndigo: true;
  }

  interface ChipPropsColorOverrides extends RiskColorOverrides {}

  interface ChipPropsSizeOverrides {
    tiny: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides extends RiskColorOverrides {}
}

/** Desktop height of the fixed app bar, used to offset what must not end up hidden under it. */
export const appBarHeight = 64;

export const createAppTheme = (locale?: string): Theme => {
  const appThemeOptions: ThemeOptions = {
    graph: {
      color: [
        themeColors.indigo[500],
        themeColors.green[400],
        themeColors.red[400],
        themeColors.yellow[400]
      ]
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            minHeight: 48,
            textTransform: 'none'
          }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none'
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none'
          }
        }
      },
      MuiToolbar: {
        styleOverrides: {
          root: {
            backgroundColor: themeColors.slate[50]
          }
        }
      },
      MuiCard: {
        styleOverrides: {
          root: {
            boxShadow: 'none'
          }
        }
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '0.9rem',
            color: themeColors.slate[800]
          },
          head: {
            fontSize: '0.8rem',
            color: '#9CA0AE'
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: themeColors.slate[50],
            border: 'none'
          },
          modal: {
            zIndex: 1201
          }
        }
      },
      MuiList: {
        defaultProps: {
          disablePadding: true
        }
      },
      MuiChip: {
        defaultProps: {
          variant: 'filled',
          size: 'small',
          deleteIcon: <Clear />
        },
        styleOverrides: {
          root: {
            height: 'unset',
            borderRadius: 6,
            padding: '0 4px',
            fontSize: '0.875rem'
          },
          filled: {
            border: '1px solid transparent',
            borderColor: 'transparent'
          },
          label: {
            width: '100%'
          },
          clickable: {
            boxShadow: 'none !important'
          },
          deleteIcon: {
            marginLeft: 2,
            transition: 'transform 120ms ease-in',
            backfaceVisibility: 'hidden',
            '& *': {
              backfaceVisibility: 'hidden' // prevents blurring on scale
            },
            '&:hover': {
              transform: 'scale(1.25)'
            }
          }
        },
        variants: [
          {
            props: { variant: 'suggestion' },
            style: {
              background: 'transparent',
              border: `1px dashed ${themeColors.slate[300]}`,
              fontWeight: 500,
              color: themeColors.slate[400],
              fontSize: '0.875rem',
              transition:
                'background-color 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, border-color 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms,box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
              '&:hover, &.Mui-focusVisible': {
                background: themeColors.indigo[50],
                borderColor: themeColors.indigo[200]
              }
            }
          },
          {
            props: { size: 'tiny' },
            style: {
              fontSize: '0.75rem',
              padding: '2px 0',
              minWidth: 'unset'
            }
          },
          {
            props: { color: 'riskHigh' },
            style: {
              fontWeight: 500,
              backgroundColor: themeColors.red[100],
              color: themeColors.red[800],
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.red[200]
              }
            }
          },
          {
            props: { color: 'riskMed' },
            style: {
              backgroundColor: themeColors.yellow[100],
              color: themeColors.yellow[800],
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.yellow[200]
              }
            }
          },
          {
            props: { color: 'riskNone' },
            style: {
              backgroundColor: themeColors.slate[100],
              color: themeColors.slate[800],
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.slate[200]
              }
            }
          },
          {
            props: { color: 'success' },
            style: {
              backgroundColor: themeColors.green[100],
              color: themeColors.green[800],
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.green[200]
              }
            }
          },
          {
            props: { color: 'success', variant: 'outlined' },
            style: {
              backgroundColor: 'transparent !important',
              color: themeColors.green[600],
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.green[50]
              }
            }
          },
          {
            props: { color: 'warning' },
            style: {
              backgroundColor: `${themeColors.orange[100]} !important`,
              color: themeColors.orange[800],
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.orange[200]
              }
            }
          },
          {
            props: { color: 'warning', variant: 'outlined' },
            style: {
              backgroundColor: 'transparent !important',
              color: themeColors.orange[600],
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.orange[50]
              }
            }
          },
          {
            props: { variant: 'empty' },
            style: {
              backgroundColor: 'transparent',
              borderColor: 'transparent',
              color: themeColors.slate[500]
            }
          },
          {
            props: { variant: 'medicationName' },
            style: {
              backgroundColor: themeColors.indigo[100],
              color: themeColors.indigo[800],
              borderColor: 'transparent',
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.indigo[200]
              }
            }
          },
          {
            props: { color: 'info' },
            style: {
              backgroundColor: themeColors.blue[100],
              color: themeColors.blue[800],
              borderColor: 'transparent',
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.blue[200]
              }
            }
          },
          {
            props: { variant: 'slateIndigo' },
            style: {
              backgroundColor: themeColors.indigo[100],
              color: themeColors.slate[800],
              borderColor: 'transparent',
              '&.MuiChip-clickable:hover': {
                backgroundColor: themeColors.indigo[200]
              }
            }
          }
        ]
      },
      MuiSelect: {
        variants: [
          {
            props: { color: 'riskHigh' },
            style: {
              color: riskStyleComponents['red'].labelColor,
              fontWeight: riskStyleComponents['red'].fontWeight,
              '& .MuiTypography-root': {
                fontWeight: 'inherit'
              },
              ...getRiskStyle('red')
            }
          },
          {
            props: { color: 'riskMed' },
            style: {
              color: riskStyleComponents['yellow'].labelColor,
              fontWeight: riskStyleComponents['red'].fontWeight,
              '& .MuiTypography-root': {
                fontWeight: 'inherit'
              },
              ...getRiskStyle('yellow')
            }
          },
          {
            props: { color: 'riskNone' },
            style: {
              color: riskStyleComponents['slate'].labelColor,
              fontWeight: riskStyleComponents['slate'].fontWeight,
              '& .MuiTypography-root': {
                fontWeight: 'inherit'
              },
              ...getRiskStyle('slate')
            }
          }
        ]
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            borderRadius: 6
          }
        },
        variants: [
          {
            props: { color: 'riskHigh' },
            style: getRiskStyle('red')
          },
          {
            props: { color: 'riskMed' },
            style: getRiskStyle('yellow')
          },
          {
            props: { color: 'riskNone' },
            style: getRiskStyle('slate')
          }
        ]
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            borderRadius: 6
          }
        },
        variants: [
          {
            props: { color: 'riskHigh' },
            style: getRiskStyle('red')
          },
          {
            props: { color: 'riskMed' },
            style: getRiskStyle('yellow')
          },
          {
            props: { color: 'riskNone' },
            style: getRiskStyle('slate')
          }
        ]
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: 6
            // ...getRiskStyle('slate')
          }
        },
        variants: [
          {
            props: { color: 'riskHigh' },
            style: getRiskStyle('red')
          },
          {
            props: { color: 'riskMed' },
            style: getRiskStyle('yellow')
          },
          {
            props: { color: 'riskNone' },
            style: getRiskStyle('slate')
          }
        ]
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            background: themeColors.slate[50]
          }
        }
      },
      MuiCircularProgress: {
        defaultProps: {
          thickness: 5,
          size: 32
        },
        styleOverrides: {
          root: {
            strokeLinecap: 'round'
          }
        }
      },
      MuiPopper: {
        defaultProps: {
          placement: 'bottom-start',
          sx: {
            width: 'fit-content!important',
            maxWidth: 300,
            boxShadow: (t) => t.shadows[6]
          }
        }
      },
      MuiTooltip: {
        defaultProps: {
          enterDelay: 500,
          enterNextDelay: 200
        },
        styleOverrides: {
          popper: {
            boxShadow: 'none'
          }
        }
      },
      MuiSnackbar: {
        styleOverrides: {
          root: {
            maxWidth: 400
          }
        }
      }
      // MuiFilledInput: {
      //   styleOverrides: {
      //     root: {
      //       backgroundColor: '#F7F8FA',
      //       borderRadius: 6,
      //       '&.Mui-disabled': {
      //         // color: 'red !important',
      //         backgroundColor: '#F7F8FA'
      //       },
      //
      //       '&:before': {
      //         display: 'none'
      //       }
      //     }
      //   }
      // }
    }
    // app-specific theming rules go here
  };

  return createBaseTheme({
    locale,
    themeOptions: appThemeOptions,
    responsiveFonts: false
  });
};

export const getRiskColor = (e?: Emphasis) => {
  switch (e) {
    case Emphasis.CRUCIAL:
      return 'riskHigh';
    case Emphasis.IMPORTANT:
      return 'riskMed';
    default:
      return 'riskNone';
  }
};

type RiskStyleComponents = {
  borderColor: string;
  borderColorHover: string;
  labelColor: string;
  fontWeight: number;
  backgroundColor: string;
};

const riskStyleComponents: Record<
  'red' | 'yellow' | 'slate',
  RiskStyleComponents
> = {
  red: {
    borderColor: themeColors.red[400],
    borderColorHover: `${themeColors.red[600]} !important`,
    labelColor: `${themeColors.red[500]} !important`,
    fontWeight: 500,
    backgroundColor: themeColors.red[50]
  },
  yellow: {
    borderColor: themeColors.yellow[500],
    borderColorHover: `${themeColors.yellow[600]} !important`,
    labelColor: `${themeColors.yellow[600]} !important`,
    fontWeight: 400,
    backgroundColor: themeColors.yellow[50]
  },
  slate: {
    borderColor: themeColors.slate[200],
    borderColorHover: `${themeColors.slate[400]} !important`,
    labelColor: `${themeColors.slate[500]} !important`,
    fontWeight: 400,
    backgroundColor: themeColors.slate[50]
  }
};

const getRiskStyle = (baseCol: 'red' | 'yellow' | 'slate') => {
  const {
    // borderColor,
    borderColorHover,
    labelColor,
    fontWeight,
    backgroundColor
  } = riskStyleComponents[baseCol];
  return {
    backgroundColor,
    zIndex: 0,
    '& fieldset': {
      borderColor: 'transparent'
    },
    '&.Mui-focused fieldset, &:hover fieldset': {
      borderColor: borderColorHover
    },
    '& label.Mui-focused, & label': {
      fontWeight: fontWeight,
      color: labelColor
    }
  };
};
